import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { copyText, deleteTranscription, fetchMarkdown, listTranscriptions, retryTranscription } from "../api";
import type { TranscriptionSummary } from "../types";
import { fmtAbsolute, fmtBytes, fmtCount, fmtDuration, fmtDurationLong, fmtRelative } from "../format";
import { Masthead } from "../components/Masthead";
import { UploadDropzone } from "../components/UploadDropzone";
import { DeleteModal } from "../components/DeleteModal";
import { Markdown } from "../components/Markdown";

export function ListPage() {
  const [items, setItems] = useState<TranscriptionSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pending, setPending] = useState<{ id: string; title: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [retrying, setRetrying] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState<Record<string, "busy" | "copied" | "failed">>({});
  const navigate = useNavigate();

  const ledger = useMemo(() => {
    if (!items?.length) return null;
    let duration = 0, words = 0, size = 0, active = 0, failed = 0;
    for (const it of items) {
      duration += it.duration_secs ?? 0;
      words += it.word_count ?? 0;
      size += it.size_bytes ?? 0;
      if (it.status === "queued" || it.status === "processing") active++;
      if (it.status === "failed") failed++;
    }
    return { duration, words, size, active, failed };
  }, [items]);

  const refresh = useCallback(async () => {
    try {
      setItems(await listTranscriptions());
      setLoadError(null);
    } catch (err) {
      if ((err as Error).message !== "unauthorized") {
        setLoadError("Couldn't reach the server.");
      }
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // Auto-refresh while jobs are active
  useEffect(() => {
    if (!items?.some((i) => i.status === "queued" || i.status === "processing")) return;
    const id = setInterval(() => { void refresh(); }, 3000);
    return () => clearInterval(id);
  }, [items, refresh]);

  const onConfirmDelete = async () => {
    if (!pending) return;
    setDeleting(true);
    try {
      await deleteTranscription(pending.id);
      setPending(null);
      await refresh();
    } catch {
      setLoadError("Delete failed — the server may be unreachable.");
    } finally {
      setDeleting(false);
    }
  };

  const onCopyMarkdown = async (id: string) => {
    setCopied((c) => ({ ...c, [id]: "busy" }));
    try {
      await copyText(await fetchMarkdown(id));
      setCopied((c) => ({ ...c, [id]: "copied" }));
    } catch {
      setCopied((c) => ({ ...c, [id]: "failed" }));
    }
    setTimeout(() => setCopied((c) => {
      const { [id]: _, ...rest } = c;
      return rest;
    }), 2000);
  };

  const onRetry = async (id: string) => {
    setRetrying((s) => new Set(s).add(id));
    try {
      await retryTranscription(id);
      await refresh();
    } catch {
      setLoadError("Retry failed — the audio may no longer be on the server.");
    } finally {
      setRetrying((s) => { const next = new Set(s); next.delete(id); return next; });
    }
  };

  return (
    <>
      <Masthead count={items?.length} />
      <UploadDropzone onUploaded={(id) => navigate(`/transcriptions/${id}`)} onChanged={refresh} />

      {loadError && (
        <div className="error-banner" role="alert">
          <span>{loadError}</span>
          <button type="button" className="btn" onClick={() => { setLoadError(null); void refresh(); }}>
            Try again
          </button>
        </div>
      )}

      {items == null && !loadError ? (
        <div className="index" aria-hidden="true">
          {Array.from({ length: 5 }, (_, i) => (
            <div className="index-row skeleton-row" key={i}>
              <span className="skeleton skeleton-num" />
              <span><span className="skeleton skeleton-title" style={{ width: `${52 - i * 7}%` }} /></span>
              <span className="skeleton skeleton-meta" />
              <span className="skeleton skeleton-status" />
            </div>
          ))}
        </div>
      ) : items != null && items.length === 0 ? (
        <div className="empty">
          <p>Nothing transcribed yet.</p>
          <p className="empty-hint">Drop an audio file above — the transcript, speakers, and search arrive on their own.</p>
        </div>
      ) : items != null ? (
        <div className="index">
          {ledger && (
            <div className="ledger">
              <span className="num">{items.length} recording{items.length === 1 ? "" : "s"}</span>
              {ledger.duration > 0 && <span className="num">{fmtDurationLong(ledger.duration)}</span>}
              {ledger.words > 0 && <span className="num">{fmtCount(ledger.words)} words</span>}
              {ledger.size > 0 && <span className="num">{fmtBytes(ledger.size)}</span>}
              {ledger.active > 0 && <span className="num ledger-active">{ledger.active} in progress</span>}
              {ledger.failed > 0 && <span className="num ledger-failed">{ledger.failed} failed</span>}
            </div>
          )}
          {items.map((it, i) => {
            const num = String(items.length - i).padStart(2, "0");
            const displayTitle = it.title?.trim() || it.filename;
            const hasCustomTitle = !!it.title && it.title.trim().length > 0 && it.title !== it.filename;
            const summary = (it.summary ?? "").trim();
            const facts = [
              (it.language_code ?? "").toUpperCase(),
              it.duration_secs != null ? fmtDuration(it.duration_secs) : "",
              it.word_count ? `${fmtCount(it.word_count)} words` : "",
            ].filter(Boolean).join(" · ");
            const provenance = [
              it.speaker_count ? `${it.speaker_count} speaker${it.speaker_count === 1 ? "" : "s"}` : "",
              it.size_bytes ? fmtBytes(it.size_bytes) : "",
            ].filter(Boolean).join(" · ");
            const copyStatus = copied[it.id];
            return (
              <div className="index-row" key={it.id}>
                <Link className="index-row-link" to={`/transcriptions/${it.id}`}>
                  <span className="index-num">№ {num}</span>
                  <span>
                    <span className="index-title">{displayTitle}</span>
                    {hasCustomTitle && <span className="index-filename">{it.filename}</span>}
                    {it.status === "failed" && it.error ? (
                      <span className="index-error">{it.error}</span>
                    ) : summary ? (
                      <span className="index-summary"><Markdown source={summary} inline /></span>
                    ) : null}
                  </span>
                  <span className="index-meta">
                    <span>{facts || "—"}</span>
                    <span>
                      {provenance && `${provenance} · `}
                      <span title={fmtAbsolute(it.created_at)}>{fmtRelative(it.created_at)}</span>
                    </span>
                  </span>
                  <span className="index-status">
                    <span className={`status ${it.status}`}>{it.status}</span>
                  </span>
                </Link>
                <span className="index-actions">
                  {it.status === "done" && (
                    <button
                      type="button"
                      className={`row-action${copyStatus === "copied" ? " ok" : ""}`}
                      disabled={copyStatus === "busy"}
                      aria-label={`Copy markdown of ${displayTitle}`}
                      onClick={(e) => { e.preventDefault(); void onCopyMarkdown(it.id); }}
                    >
                      {copyStatus === "copied" ? "copied ✓" : copyStatus === "failed" ? "copy failed" : "copy md"}
                    </button>
                  )}
                  {it.status === "failed" && (
                    <button
                      type="button"
                      className="row-action"
                      disabled={retrying.has(it.id)}
                      onClick={(e) => { e.preventDefault(); void onRetry(it.id); }}
                    >
                      {retrying.has(it.id) ? "retrying…" : "retry"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="row-action danger"
                    aria-label={`Delete ${displayTitle}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setPending({ id: it.id, title: displayTitle });
                    }}
                  >delete</button>
                </span>
              </div>
            );
          })}
        </div>
      ) : null}

      {pending && (
        <DeleteModal
          title={pending.title}
          busy={deleting}
          onCancel={() => setPending(null)}
          onConfirm={onConfirmDelete}
        />
      )}
    </>
  );
}
